import forEach from 'lodash/forEach';
import systemPanels from './systemPanels/systemPanelConstants';
import {
    getWidgetConfiguration,
    setupViewAndWidgetConfigurations,
    setupWidgetConfiguration,
} from '../viewDashboards/utils/configurationAndValidationUtils';
import {
    defaultLandingPageWidth,
    systemWidgetType,
} from './landingPageConstants';
import { getRequiredFiltersWithOneOption } from '../viewDashboards/dashboardUtils';
import {
    buildWidgetMetricRequests,
    mergeFilterValues,
    mergeRecordLimit,
    mergeTimeAndPrecision,
    mergeWhereClausesUniqueMeasures,
} from '../viewDashboards/utils/metricRequestUtils';

export function getInitialLandingPageConfig(dashboardLabels) {
    return {
        title: dashboardLabels.welcome,
        openLinksInNewTab: true,
        containerStyles: {
            maxWidth: defaultLandingPageWidth,
        },
        panels: [
            {
                panelIndex: 0,
                landingPageWidget: {
                    type: 'systemWidget',
                    configurationKey: 'systemLinks',
                },
            },
            {
                panelIndex: 1,
                landingPageWidget: {
                    type: 'systemWidget',
                    configurationKey: 'userFavorites',
                },
            },
        ],
    };
}

export function addDefaultedLandingPageValues(
    initialLandingPageConfig,
    defaultLandingPageConfig
) {
    return {
        ...initialLandingPageConfig,
        ...defaultLandingPageConfig,
    };
}

export function applyLandingPageConfig(
    defaultLandingPageConfig,
    landingPageConfig
) {
    return {
        ...defaultLandingPageConfig,
        ...landingPageConfig,
    };
}

export function applyPopulatedPanels(landingPageConfig, populatedPanels) {
    return {
        ...landingPageConfig,
        panels: populatedPanels,
    };
}

export function getSystemPanel(configurationKey) {
    return systemPanels.find(
        (x) => x.landingPageWidget.configurationKey === configurationKey
    );
}

export function getAllViewsFromDashboard(dashboardKey, allDashboardConfigs) {
    const dashboard = allDashboardConfigs.find((x) => x.id === dashboardKey);

    if (!dashboard) {
        return null;
    }

    return dashboard.configuration?.views;
}

export function getLandingPageWidget(widgetId, viewConfig) {
    // if widgetId is null, need to grab the old landing page config
    if (widgetId === null || widgetId === undefined) {
        return viewConfig?.landingPageWidget;
    }

    const widget = viewConfig?.widgets.find(
        (x) => x.landingPageConfig?.id === widgetId
    );

    return widget;
}

export function populateSystemPanel(panel, widgetRenderers) {
    const systemPanel = getSystemPanel(
        panel.landingPageWidget.configurationKey
    );

    return {
        panelIndex: panel.panelIndex,
        refreshRateMilliseconds: systemPanel.refreshRateMilliseconds,
        landingPageWidget: {
            ...systemPanel.landingPageWidget,
            configuration: getWidgetConfiguration(
                widgetRenderers,
                systemPanel.landingPageWidget
            ),
        },
    };
}

export function minimizeDashboardPanel(panel) {
    const {
        panelIndex,
        dashboardKey,
        viewKey,
        landingPageWidgetId,
        savedFilterKey,
    } = panel;
    return {
        panelIndex,
        dashboardKey,
        viewKey,
        landingPageWidgetId,
        savedFilterKey,
    };
}

export function minimizeSystemPanel(panel) {
    const {
        panelIndex,
        landingPageWidget: { configurationKey, panelTitleOverride, type },
    } = panel;

    return {
        panelIndex,
        landingPageWidget: {
            configurationKey,
            panelTitleOverride,
            type,
        },
    };
}

export function minimizePanelConfigsForSave(panels) {
    return panels.map((panel) =>
        panel.landingPageWidget?.type === systemWidgetType
            ? minimizeSystemPanel(panel)
            : minimizeDashboardPanel(panel)
    );
}

export function getDashboardFilterConfigurations(configuration) {
    return configuration.views.reduce(
        (result, view) => ({
            ...result,
            ...view.filterConfigurations,
        }),
        {}
    );
}

export function widgetWithRequiredSingleOptionsFilters(
    landingPageWidget,
    filterConfigurations,
    requiredFilterKeys,
    selectOptions
) {
    return {
        ...landingPageWidget,
        filterValues: {
            ...getRequiredFiltersWithOneOption(
                filterConfigurations,
                requiredFilterKeys,
                selectOptions
            ),
            ...landingPageWidget.filterValues,
        },
    };
}

export function getAllSelectOptionApiKeys(dashboardConfigs) {
    return dashboardConfigs.reduce((result, dashboardConfig) => {
        dashboardConfig.configuration.views.forEach((view) => {
            if (view.filterConfigurations) {
                forEach(view.filterConfigurations, (filterConfig) =>
                    result.push(filterConfig.apiKey)
                );
            }
        });

        return result;
    }, []);
}

function mergeDashboardAndSavedFilters(viewConfig, savedFilter) {
    return {
        timeAndPrecision: mergeTimeAndPrecision(
            viewConfig.timeAndPrecision,
            savedFilter?.timeAndPrecision
        ),
        filterValues: mergeFilterValues(
            viewConfig.filterValues,
            savedFilter?.filterValues
        ),
        whereClauses: mergeWhereClausesUniqueMeasures(
            viewConfig.whereClauses,
            savedFilter?.whereClauses
        ),
        recordLimit: mergeRecordLimit(
            viewConfig.recordLimit,
            savedFilter?.recordLimit
        ),
    };
}

// eslint-disable-next-line complexity
export function populateWidgetPanel(
    panel,
    allDashboardConfigs,
    allSavedFilters,
    widgetRenderers,
    selectOptions
) {
    const viewsConfigs = getAllViewsFromDashboard(
        panel.dashboardKey,
        allDashboardConfigs
    );

    if (!viewsConfigs) {
        return null;
    }

    const viewConfig = !panel.viewKey
        ? viewsConfigs?.[0]
        : viewsConfigs?.find((x) => x.viewKey === panel.viewKey);

    const setupViewConfig = setupViewAndWidgetConfigurations(
        widgetRenderers,
        viewConfig
    );

    const landingPageWidget = getLandingPageWidget(
        panel.landingPageWidgetId,
        setupViewConfig
    );

    if (!landingPageWidget) {
        return null;
    }

    const savedFilter = allSavedFilters.find(
        (x) => x.key === panel.savedFilterKey
    );

    if (panel.savedFilterKey && !savedFilter) {
        return null;
    }

    const { landingPageConfig, ...restOfWidget } = landingPageWidget;
    const widgetWithLandingPageConfigApplied = {
        ...restOfWidget,
        ...landingPageConfig,
    };

    const dashboardLevelFilters = mergeDashboardAndSavedFilters(
        setupViewConfig,
        savedFilter?.value
    );

    const metricRequests = buildWidgetMetricRequests(
        widgetWithLandingPageConfigApplied,
        dashboardLevelFilters.timeAndPrecision,
        dashboardLevelFilters.filterValues,
        dashboardLevelFilters.whereClauses,
        dashboardLevelFilters.recordLimit,
        false
    );

    const widget = setupWidgetConfiguration(widgetRenderers, {
        ...widgetWithLandingPageConfigApplied,
        metricRequests,
    });

    const widgetWithRequiredSingleOptionFilters =
        widgetWithRequiredSingleOptionsFilters(
            widget,
            setupViewConfig.filterConfigurations,
            setupViewConfig.requiredFilterKeys,
            selectOptions
        );

    return {
        panelIndex: panel.panelIndex,
        dashboardKey: panel.dashboardKey,
        dashboardTitle: setupViewConfig.title,
        viewKey: panel.viewKey,
        displayViewTitle: viewsConfigs.length > 1,
        savedFilterKey: panel.savedFilterKey,
        savedFilterDescription: savedFilter?.name,
        landingPageWidget: widgetWithRequiredSingleOptionFilters,
        landingPageWidgetId: panel.landingPageWidgetId,
        friendlyColumnLabels: setupViewConfig.friendlyColumnLabels,
        refreshRateMilliseconds: setupViewConfig.refreshRateMilliseconds,
    };
}
